// Multi-adapter suburb-profile consensus. Domain and realestate.com.au
// can report different medians/growth for the same (suburb, month).
// Per the architectural decision in the plan, per-adapter rows stay
// untouched in the database — this helper layers a sales-volume-
// weighted aggregate on top for display + valuation-algo consumption,
// and flags significant disagreements so the UI can surface them.

export interface ConsensusInputRow {
  adapterSlug: string;
  adapterName?: string | null;
  snapshotMonth: string;
  medianHousePrice?: number | null;
  medianUnitPrice?: number | null;
  annualGrowthPct?: number | null;
  fiveYearGrowthPct?: number | null;
  houseSalesVolume12mo?: number | null;
  unitSalesVolume12mo?: number | null;
  rentalYieldHousePct?: number | null;
  daysOnMarketHouse?: number | null;
}

/**
 * Per-field weight source. Unit metrics weight by unit sales so a
 * suburb where Domain reports 55 units and REA reports 68 produces a
 * unit-sales-weighted consensus, not a house-sales-weighted one. Falls
 * back to house sales (or 1) if the field-specific weight is missing.
 */
const UNIT_FIELDS = new Set<keyof ConsensusInputRow>(["medianUnitPrice"]);

export interface ConsensusFieldResult {
  /** Sales-volume-weighted aggregate. Null when nobody reported it. */
  value: number | null;
  /** Per-adapter contributions for the field. */
  perAdapter: Array<{ adapterSlug: string; value: number; weight: number; deltaPct: number | null }>;
  /** Highest pairwise deviation (%) from the weighted mean. Null when only 1 source. */
  maxDeviationPct: number | null;
  /** Δ > threshold flag — UI badge condition. */
  significantDisagreement: boolean;
}

export interface ConsensusResult {
  /** Snapshots that contributed (same month, deduped by adapter). */
  contributors: Array<{ adapterSlug: string; adapterName?: string | null; snapshotMonth: string }>;
  medianHousePrice: ConsensusFieldResult;
  medianUnitPrice: ConsensusFieldResult;
  annualGrowthPct: ConsensusFieldResult;
  fiveYearGrowthPct: ConsensusFieldResult;
  rentalYieldHousePct: ConsensusFieldResult;
  daysOnMarketHouse: ConsensusFieldResult;
  /** Sum of houseSalesVolume12mo across contributors (NOT a consensus). */
  combinedHouseSalesVolume12mo: number | null;
}

const DEFAULT_DISAGREE_THRESHOLD = 0.05; // 5%

function weightedConsensus(
  rows: ConsensusInputRow[],
  field: keyof ConsensusInputRow,
  threshold = DEFAULT_DISAGREE_THRESHOLD,
): ConsensusFieldResult {
  const useUnitWeight = UNIT_FIELDS.has(field);
  const contributing: Array<{ adapterSlug: string; value: number; weight: number }> = [];
  for (const r of rows) {
    const raw = r[field];
    if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
    // Match the weight to the metric's market: unit metrics weight by
    // unit sales, everything else by house sales. Fall back to 1 when a
    // row lacks volume info so we don't silently shrink the panel.
    const primary = useUnitWeight ? r.unitSalesVolume12mo : r.houseSalesVolume12mo;
    const weight =
      typeof primary === "number" && primary > 0
        ? primary
        : typeof r.houseSalesVolume12mo === "number" && r.houseSalesVolume12mo > 0
          ? r.houseSalesVolume12mo
          : 1;
    contributing.push({ adapterSlug: r.adapterSlug, value: raw, weight });
  }
  if (contributing.length === 0) {
    return {
      value: null,
      perAdapter: [],
      maxDeviationPct: null,
      significantDisagreement: false,
    };
  }
  const totalWeight = contributing.reduce((s, c) => s + c.weight, 0);
  const weightedMean =
    contributing.reduce((s, c) => s + c.value * c.weight, 0) / totalWeight;

  // Deviation expressed as relative %. Guard against division by zero
  // when the weighted mean rounds to exactly 0 (growth-pct cases).
  const baseForPct = Math.abs(weightedMean) > 1e-9 ? Math.abs(weightedMean) : 1;
  const perAdapter = contributing.map((c) => {
    const delta = (c.value - weightedMean) / baseForPct;
    return {
      adapterSlug: c.adapterSlug,
      value: c.value,
      weight: c.weight,
      deltaPct: Math.round(delta * 10000) / 10000,
    };
  });
  const maxDeviation =
    perAdapter.length > 1
      ? Math.max(...perAdapter.map((p) => Math.abs(p.deltaPct ?? 0)))
      : null;
  return {
    value: weightedMean,
    perAdapter,
    maxDeviationPct: maxDeviation,
    significantDisagreement: maxDeviation !== null && maxDeviation > threshold,
  };
}

/**
 * Build a sales-volume-weighted consensus across multiple adapters'
 * suburb-profile snapshots for the SAME month. The caller is
 * responsible for filtering input to a single month — this function
 * does not validate that all rows have the same `snapshotMonth`.
 *
 * The result preserves per-adapter contributions so the UI can render
 * "Domain says X, REA says Y, weighted = Z" side-by-side and badge
 * meaningful disagreements (>5% by default).
 */
export function buildSuburbProfileConsensus(
  rows: ConsensusInputRow[],
  options: { disagreementThreshold?: number } = {},
): ConsensusResult {
  const threshold = options.disagreementThreshold ?? DEFAULT_DISAGREE_THRESHOLD;
  const contributors = rows.map((r) => ({
    adapterSlug: r.adapterSlug,
    adapterName: r.adapterName ?? null,
    snapshotMonth: r.snapshotMonth,
  }));
  const combinedHouseSalesVolume12mo = rows.reduce(
    (s, r) =>
      s + (typeof r.houseSalesVolume12mo === "number" ? r.houseSalesVolume12mo : 0),
    0,
  );
  return {
    contributors,
    medianHousePrice: weightedConsensus(rows, "medianHousePrice", threshold),
    medianUnitPrice: weightedConsensus(rows, "medianUnitPrice", threshold),
    annualGrowthPct: weightedConsensus(rows, "annualGrowthPct", threshold),
    fiveYearGrowthPct: weightedConsensus(rows, "fiveYearGrowthPct", threshold),
    rentalYieldHousePct: weightedConsensus(rows, "rentalYieldHousePct", threshold),
    daysOnMarketHouse: weightedConsensus(rows, "daysOnMarketHouse", threshold),
    combinedHouseSalesVolume12mo:
      combinedHouseSalesVolume12mo > 0 ? combinedHouseSalesVolume12mo : null,
  };
}
